// =====================================================
// 📁 QuotationPriceCheckSearch.tsx
// =====================================================

import { useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { Search } from 'lucide-react';

import { useBranch } from '../../../../../hooks/branches/useBranch';
import useProductCatalog from '../../../../../hooks/productpanel/useProductCatalog';

const QuotationPriceCheckSearch = () => {
  // =====================================================
  // 🔹 Refs
  // =====================================================

  const inputRef = useRef<HTMLInputElement>(null);

  // =====================================================
  // 🔹 Branches
  // =====================================================

  const { branchDropdown, fetchBranchDropdown } =
    useBranch();

  // =====================================================
  // 🔹 Load Branches
  // =====================================================

  useEffect(() => {
    fetchBranchDropdown();
  }, []);

  // =====================================================
  // 🔹 Active Branch
  // =====================================================

  const branchId =
    branchDropdown.length > 0
      ? branchDropdown[0].id
      : undefined;

  const isSearchReady = !!branchId;

  // =====================================================
  // 🔹 Product Catalog
  // =====================================================

  const {
    fetchFilteredProducts,
    setSelectedCategory,
    pageSize,
  } = useProductCatalog(branchId);

  // =====================================================
  // 🔹 State
  // =====================================================

  const [query, setQuery] = useState('');

  // =====================================================
  // 🔹 Debounced Search
  // =====================================================

  const debouncedSearch = debounce(
    (text: string) => {
      if (!branchId) {
        return;
      }

      setSelectedCategory(null);

      const filters = text.trim()
        ? {
            search: text,
            branchId,
          }
        : {
            branchId,
          };

      fetchFilteredProducts(
        filters,
        0,
        pageSize
      );
    },
    400
  );

  // =====================================================
  // 🔹 Search Effect
  // =====================================================

  useEffect(() => {
    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, branchId]);

  // =====================================================
  // 🔹 Keyboard Shortcuts
  // =====================================================

  useEffect(() => {
    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (event.key === 'F3') {
        event.preventDefault();

        if (!isSearchReady) {
          return;
        }

        inputRef.current?.focus();
        inputRef.current?.select();
      }

      if (event.key === 'Delete') {
        if (!isSearchReady) {
          return;
        }

        if (
          document.activeElement !==
          inputRef.current
        ) {
          return;
        }

        setQuery('');
      }

      if (event.key === 'Escape') {
        if (!isSearchReady) {
          return;
        }

        setQuery('');
        inputRef.current?.blur();
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [isSearchReady]);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div className="w-full">
      <div
        className={`
          relative

          flex
          h-[44px]
          items-center

          overflow-hidden

          rounded-xl
          border

          shadow-1
          transition-all
          duration-200

          ${
            isSearchReady
              ? `
                border-slate-300
                bg-white

                focus-within:border-primary
                focus-within:ring-4
                focus-within:ring-primary/15

                hover:border-primary/50
                hover:shadow-2
              `
              : `
                cursor-not-allowed

                border-slate-200
                bg-slate-100
              `
          }

          dark:border-form-strokedark
          dark:bg-boxdark-2

          dark:focus-within:border-primary
          dark:focus-within:ring-4
          dark:focus-within:ring-primary/20

          dark:hover:border-primary/50
        `}
      >
        {/* 🔹 Search Icon */}

        <div
          className={`
            flex
            h-full
            w-[44px]
            shrink-0

            items-center
            justify-center

            border-r

            ${
              isSearchReady
                ? `
                  border-primary
                  bg-primary
                  text-white
                `
                : `
                  border-slate-300
                  bg-slate-200
                  text-slate-500

                  dark:border-strokedark
                  dark:bg-meta-4
                  dark:text-bodydark
                `
            }
          `}
        >
          <Search className="h-4 w-4" />
        </div>

        {/* 🔹 Input */}

        <input
          ref={inputRef}
          type="text"
          value={query}
          disabled={!isSearchReady}
          placeholder="Buscar por nombre, código, SKU o código de barras..."
          onChange={(e) =>
            setQuery(e.target.value)
          }
          className={`
            h-full
            w-full

            bg-gray-3
            
            dark:bg-transparent

            px-4
            pr-16

            text-sm
            font-medium

            outline-none

            ${
              isSearchReady
                ? `
                  text-slate-700
                  placeholder:text-slate-400
                `
                : `
                  cursor-not-allowed

                  text-slate-400
                  placeholder:text-slate-300
                `
            }

            dark:text-white
            dark:placeholder:text-bodydark2
          `}
        />

        {/* 🔹 Shortcut */}

        {isSearchReady && (
          <div
            className="
              absolute
              right-3

              rounded-md
              border

              border-slate-200
              bg-slate-50

              px-2
              py-1

              text-[10px]
              font-medium

              text-slate-400

              dark:border-form-strokedark
              dark:bg-boxdark
              dark:text-bodydark2
            "
          >
            F3
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotationPriceCheckSearch;